import { Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Button from "../../components/GeneralComponents/Button";
import DomeCareTitle from "../../components/GeneralComponents/DomeCareTitle";
import ErrorsWrapper from "../../components/GeneralComponents/ErrorsWrapper";
import GeneralInput from "../../components/GeneralComponents/GeneralInput";
import PasswordStrengthIndicator from "../../components/GeneralComponents/PasswordStrengthIndicator";
import { useAuthContext } from "../../context/AuthContext";
import history from "../../hooks/History";
import useToast from "../../hooks/useToast";
import {
  login,
  setIsAuthenticated,
  setIsUserForgettingPassword,
  setPasswordReducer,
  setRegistrationStep,
  setRole,
  setUserOldPassword,
} from "../../redux/slices/authSlice";
import { ToggleShowGeneralToast } from "../../redux/slices/generalToastSlice";
import { RootState } from "../../redux/store";
import { CreatePasswordFormValues } from "../../types";
import { Token } from "../../types/api";
import { getTimezoneOffset } from "../../utils/Functions";
import { useChangePassword } from "../../utils/api/AuthAPIs/useChangePassword";
import { useCreatePassword } from "../../utils/api/AuthAPIs/useCreatePassword";
import { useProfileForgetPasswordResetPassword } from "../../utils/api/AuthAPIs/useForgetPasswordResetPassword";
import {
  BASE_LOCAL_IMAGES_DIR,
  DefaultNavigateDelay,
  GlobalToasterDuration,
  ToasterSuccessImageUrl,
} from "../../utils/constants/CommonConstants";
import useCreatePasswordSchema from "../../utils/validations/auth/CreatePasswordSchema";
import { forget_password_IMG } from "../../utils/constants/Images";
import { setStaffDocument } from "../../redux/slices/commonSlice";
import { useCompleteStaff } from "../../utils/api/AuthAPIs/useCompleteStaff";

const initialValues: CreatePasswordFormValues = {
  password: "",
};

interface CreatePasswordProps {
  OriginType: string;
}

const CreatePassword: React.FC<CreatePasswordProps> = ({ OriginType }) => {
  const { showToast } = useToast();
  const dispatch = useDispatch();
  // const navigate = useNavigate();
  const { changeShowLeaveAuthRoutePopupAuthState, showLeaveAuthRoutePopup } =
    useAuthContext();
  const createPasswordSchema = useCreatePasswordSchema();
  const { t, i18n } = useTranslation("translation");
  const { staffDocument } = useSelector((state: RootState) => state.common);

  const {
    userId,
    otp,
    user,
    createPasswordOrigin,
    isChangingPassword,
    oldUserPassword,
  } = useSelector((state: RootState) => state.auth);
  const { ProfileUserInfo } = useSelector((state: RootState) => state.profile);
  const isPharmacy = user?.currentFacility?.FacilityType?.Code === "pharmacy";
  //?=======================States================================
  const [password, setPassword] = useState<string | null>(null);

  //?============POST Create Password Form=============

  const {
    status: CreatePasswordStatus,
    error: CreatePasswordError,
    mutate: CreatePasswordMutate,
  } = useCreatePassword();

  const {
    status: CompleteStaffStatus,
    error: CompleteStaffError,
    mutate: CompleteStaffMutate,
  } = useCompleteStaff();

  const {
    status: ProfileForgetPasswordResetPasswordStatus,
    error: ProfileForgetPasswordResetPasswordError,
    mutate: ProfileForgetPasswordResetPasswordMutate,
  } = useProfileForgetPasswordResetPassword();

  const {
    status: ChangePasswordStatus,
    mutate: ChangePasswordMutate,
    error: ChangePasswordError,
  } = useChangePassword();

  //?============Submit Create Password Form=============
  const handleSubmit = async () => {
    if (OriginType === "account" && isChangingPassword) {
      ChangePasswordMutate(
        {
          payload: {
            NewPassword: password,
            OldPassword: oldUserPassword,
          },
          token: user?.token,
        },
        {
          onSuccess: (response) => {
            if (response.Status) {
              changeShowLeaveAuthRoutePopupAuthState(false);
              dispatch(
                setIsAuthenticated({
                  isAuthenticated: true,
                }),
              );
              dispatch(
                setPasswordReducer({
                  password: password,
                  token: user?.token,
                }),
              );
              dispatch(setUserOldPassword(password));
              dispatch(
                ToggleShowGeneralToast({
                  ToasterContent: {
                    title: t("update_password_successfully"),
                    desc: t("update_password_successfully_desc"),
                    duration: GlobalToasterDuration,
                    img: ToasterSuccessImageUrl,
                    position: "top-right",
                    type: "success",
                  },
                }),
              );
              setTimeout(() => {
                history.push("/auth/login");
              }, DefaultNavigateDelay);
            }
          },
        },
      );
    } else {
      if (createPasswordOrigin === "register") {
        CreatePasswordMutate(
          {
            Id: userId,
            Language: i18n.language.slice(0, 2),
            Password: password,
            TimeZone: getTimezoneOffset(),
            VerificationCode: otp,
          },
          {
            onSuccess: (response) => {
              if (response.Status) {
                changeShowLeaveAuthRoutePopupAuthState(false);

                const token: Token = response.Data?.Token;
                dispatch(
                  setPasswordReducer({
                    password: password,
                    token: token,
                  }),
                );
                setTimeout(() => {
                  history.push("/auth/facility-type");
                  // navigate("/auth/facility-type", { replace: true });
                }, DefaultNavigateDelay);
              }
            },
          },
        );
      } else if (createPasswordOrigin === "forgotPassword") {
        ProfileForgetPasswordResetPasswordMutate(
          {
            payload: {
              Id: userId,
              Language: i18n.language.slice(0, 2),
              Password: password,
              TimeZone: getTimezoneOffset(),
              VerificationCode: otp,
              FullName: ProfileUserInfo.FullName
                ? ProfileUserInfo.FullName
                : null,
            },
          },
          {
            onSuccess: (response) => {
              if (response.Status) {
                changeShowLeaveAuthRoutePopupAuthState(false);
                const token: Token = response.Data?.Token;
                dispatch(
                  setPasswordReducer({
                    password: password,
                    token: token,
                  }),
                );
                dispatch(setIsUserForgettingPassword(false));
                showToast(
                  t("forgot_password_success"),
                  t("forgot_password_success_desc"),
                  "success",
                  GlobalToasterDuration,
                  "bottom-right",
                  ToasterSuccessImageUrl,
                );
                setTimeout(() => {
                  history.push("/auth/login");
                }, DefaultNavigateDelay);
              }
            },
          },
        );
      } else if (createPasswordOrigin === "NewStaff" && !isPharmacy) {
        CreatePasswordMutate(
          {
            Id: userId,
            Language: i18n.language.slice(0, 2),
            Password: password,
            TimeZone: getTimezoneOffset(),
            VerificationCode: otp,
            FullName: localStorage.getItem("full_name_congrats_page"),
          },
          {
            onSuccess: (response) => {
              changeShowLeaveAuthRoutePopupAuthState(false);
              if (response.Status) {
                const userData = response.Data;
                const FacilityType =
                  userData.CurrentFacility.FacilityType?.Code;
                if (!userData) {
                  console.error("User data is null or undefined");
                  return;
                }
                const currentFacility = userData.CurrentFacility || {};
                const loginPayload = {
                  id: userData?.Id,
                  phoneNumber: userData?.PhoneNumber,
                  fullName: userData?.FullName,
                  token: userData?.Token,
                  isVerified: userData?.IsVerified,
                  currentFacility: currentFacility,
                };
                const token: Token = userData?.Token;
                dispatch(login(loginPayload));
                dispatch(setRole(FacilityType));
                dispatch(
                  setPasswordReducer({
                    password: password,
                    token: token,
                  }),
                );
                dispatch(setRegistrationStep("register"));
                localStorage.removeItem("full_name_congrats_page");
                setTimeout(() => {
                  history.push("/");
                  window.location.reload();
                }, DefaultNavigateDelay);
              }
            },
          },
        );
      } else if (createPasswordOrigin === "NewStaff" && isPharmacy) {
        const formData = new FormData();
        formData.append("Id", userId ? userId.toString() : "");
        formData.append("Language", i18n.language.slice(0, 2));
        formData.append("Password", password ? password : "");
        formData.append("TimeZone", getTimezoneOffset());
        formData.append("VerificationCode", otp ? otp : "");
        formData.append(
          "FullName",
          localStorage.getItem("full_name_congrats_page") || "",
        );
        if (staffDocument) {
          formData.append("File", staffDocument);
        }

        CompleteStaffMutate(
          {
            payload: formData,
          },
          {
            onSuccess: (response) => {
              changeShowLeaveAuthRoutePopupAuthState(false);
              if (response.Status) {
                const userData = response.Data;
                const FacilityType =
                  userData.CurrentFacility.FacilityType?.Code;
                if (!userData) {
                  console.error("User data is null or undefined");
                  return;
                }
                const currentFacility = userData.CurrentFacility || {};
                const loginPayload = {
                  id: userData?.Id,
                  phoneNumber: userData?.PhoneNumber,
                  fullName: userData?.FullName,
                  token: userData?.Token,
                  isVerified: userData?.IsVerified,
                  currentFacility: currentFacility,
                };
                const token: Token = userData?.Token;
                dispatch(login(loginPayload));
                dispatch(setRole(FacilityType));
                dispatch(
                  setPasswordReducer({
                    password: password,
                    token: token,
                  }),
                );
                dispatch(setRegistrationStep("register"));
                localStorage.removeItem("full_name_congrats_page");
                dispatch(setStaffDocument(null));
                setTimeout(() => {
                  history.push("/");
                  window.location.reload();
                }, DefaultNavigateDelay);
              }
            },
          },
        );
      }
    }
  };
  //?============Ensure Remove OTP Duration=============

  useEffect(() => {
    changeShowLeaveAuthRoutePopupAuthState(true);
    localStorage.removeItem("otpDuration");
  }, []);

  return (
    <article className="flex w-full flex-col gap-6 px-8 py-10 xl:w-[75%]">
      <div className="flex flex-col gap-6">
        {/* <DomeCareTitle /> */}
        <div className="flex flex-col gap-2">
          <p className="flex flex-col items-center justify-center gap-8 py-8 text-center text-2xl font-bold text-headingTextColor">
            <img
              src={`${BASE_LOCAL_IMAGES_DIR}/${forget_password_IMG}`}
              alt=""
              className="h-20 w-20"
            />
            {isChangingPassword && OriginType === "account"
              ? t("update_your_password")
              : t("create_new_password")}{" "}
            🔒
          </p>
          <p className="mx-auto w-4/5 text-center text-base text-textColor">
            {t("Please_type_something_you_will_remember")}
          </p>
        </div>
      </div>
      <Formik
        initialValues={initialValues}
        validationSchema={createPasswordSchema}
        onSubmit={handleSubmit}
      >
        {({ values, handleChange, handleSubmit }) => {
          setPassword(values.password);
          return (
            <Form
              className="flex flex-col gap-5"
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleSubmit();
                }
              }}
            >
              <div className="flex flex-col gap-5">
                <GeneralInput
                  type="password"
                  name="password"
                  placeholder={t("new_password")}
                  required={true}
                  value={values.password}
                  onChange={handleChange}
                />
                <div className="flex flex-col">
                  <PasswordStrengthIndicator password={values.password} />
                </div>
              </div>
              <Button
                title={
                  isChangingPassword && OriginType === "account"
                    ? t("updatePassword")
                    : CreatePasswordStatus === "pending" ||
                        ProfileForgetPasswordResetPasswordStatus ===
                          "pending" ||
                        ChangePasswordStatus === "pending" ||
                        CompleteStaffStatus === "pending"
                      ? t("loading")
                      : t("create_password")
                }
                type="submit"
                variant={values.password !== "" ? "primary" : "secondary"}
                extraClassName={values.password !== "" ? "shadow-cta" : ""}
                disabled={
                  CreatePasswordStatus === "pending" ||
                  ProfileForgetPasswordResetPasswordStatus === "pending" ||
                  ChangePasswordStatus === "pending" ||
                  CompleteStaffStatus === "pending"
                }
              />
            </Form>
          );
        }}
      </Formik>

      {/* //?=============Error Boundries==================== */}
      {CreatePasswordError ? (
        <ErrorsWrapper message={CreatePasswordError?.message} />
      ) : null}
      {ChangePasswordError ? (
        <ErrorsWrapper message={ChangePasswordError?.message} />
      ) : null}
      {ProfileForgetPasswordResetPasswordError ? (
        <ErrorsWrapper
          message={ProfileForgetPasswordResetPasswordError?.message}
        />
      ) : null}
      {CompleteStaffError ? (
        <ErrorsWrapper message={CompleteStaffError?.message} />
      ) : null}
    </article>
  );
};

export default CreatePassword;
